import { useEffect, useRef, useState } from "react";
import { useKeyboardViewportFix } from "../../hooks/useKeyboardViewportFix";
import { openTgChat, openTgChatByPhone } from "../../lib/telegram";
import { api } from "../../api/client";
import { Switch, FlagToggle, ChannelPicker, PhoneInput, AutoGrowTextarea, GameDotsFor } from "../shared";
import { BookingFormPads, type PadField } from "./BookingFormPads";
import { blurSystemField, blurIfActive, scrollCommentAboveDock } from "./padHost";
import type { BookingFormProps } from "./bookingFormProps";
import { SlotPicker } from "./SlotPicker";
import { SourcePicker } from "./SourcePicker";
import { MkPicker } from "./MkPicker";
import { SpecVersionPicker } from "./SpecVersionPicker"; import { LearningPlayersRow } from "./LearningPlayersRow";
import { AuditHistory } from "./AuditHistory";
import css from "./BookingForm.module.css";

/**
 * BookingModal body — per screen-03-dc-booking-modal эталон (Frame 1).
 *
 *   Row 1 (.rowType):     [bilead ЛИД/БРНЬ] [time slot ▼] [source ▼]
 *   Row 2 (.rowInputs):   [Кол-в] [Имя] [+7 9...]
 *   Row 3 (.rowTea):      [ЧАЙНАЯ lbl] [switch] [icon] [3 mkCell]
 *   Row 4 (.flagsRow):    [Здтк+amt] [Класс] [ДР] [Учеб]
 *   Row 5 (.flagsRow3):   [!!!] [КНКР] [СпцВ] [—]
 *   Row 6:                [@telegram]
 *   Row 7 (.channelRow):  КАНАЛ  [TG][VK][MAX]
 *   Row 8:                [Комментарий]
 *
 * Пропсы — bookingFormProps.ts (вынесены 1:1); ввод — БМ5 + A46-коррекция:
 * имя — клавиатура ОС, комментарий — docked TextPad в настоящем поле (06.09),
 * остальные поля — пады BookingFormPads; поля-хосты помечены data-pad-host.
 */
export type { BookingFormProps } from "./bookingFormProps";

export function BookingForm(p: BookingFormProps) {
  const isLead = p.formType === "lead", staffGame = p.isLearning && !!p.learning?.ids.length; /* 13.10: учебная игра с сотрудниками — имя/телефон не обязательны, красным не подсвечиваем */
  /* БМ5/A46-коррекция: поле→пад (+якорь M.5); имя — СИС; комментарий с 14.08 — docked M.6 (айфон возил кнопки поверх поля). */
  const [dockH, setDockH] = useState(0); /* 02.09: высота docked-клавиатуры от TextPad */
  const [pad, setPad] = useState<PadField>(null),
    [padAnchor, setPadAnchor] = useState<HTMLElement | null>(null);
  const nameRef = useRef<HTMLInputElement>(null), commentRef = useRef<HTMLTextAreaElement>(null);
  /* 06.09 (владелец): открыть пад = сначала увести фокус из нативного поля (иначе к кастомной
     клавиатуре добавлялась системная); закрыть = снять каретку с комментария, если фокус ещё в нём. */
  const openPad = (f: PadField, anchor?: HTMLElement | null) => {
    blurSystemField();
    if (anchor !== undefined) setPadAnchor(anchor);
    setPad(f);
  };
  const closePad = () => { setPad(null); blurIfActive(commentRef.current); };
  /* 02.09 (владелец): поле комментария всегда видно над docked-клавиатурой — на КАЖДОЕ
     изменение (буква/пробел/⏎/стирание). 06.09 (владелец): пока палец на поле, его НЕ двигаем —
     браузер ставит каретку в место тапа на touchend (~100 мс после pointerdown), сдвинутое поле
     дало бы каретку не там, где клик; докрутка после тапа — в onClick поля (каретка уже стоит). */
  const commentDown = useRef(false);
  useEffect(() => {
    if (pad !== "comment") return;
    const el = commentRef.current; if (!el) return;
    const t = window.setTimeout(() => { if (!commentDown.current) scrollCommentAboveDock(el, dockH); }, 40);
    return () => window.clearTimeout(t);
  }, [pad, p.comment, dockH]);
  useKeyboardViewportFix(nameRef);
  const [slotPickerOpen, setSlotPickerOpen] = useState(false);
  const [sourcePickerOpen, setSourcePickerOpen] = useState(false);
  /* long-press по ✈ → кнопка «Открыть переписку» (25.08); тап вне — закрыть */
  const [chatPop, setChatPop] = useState(false);
  useEffect(() => {
    if (!chatPop) return;
    const onDown = (e: PointerEvent) => { if (!(e.target as HTMLElement | null)?.closest?.("[data-chatpop]")) setChatPop(false); };
    document.addEventListener("pointerdown", onDown, true);
    return () => document.removeEventListener("pointerdown", onDown, true);
  }, [chatPop]);
  /* Which mk-cell index (0/1/2) is showing its picker, or null. */
  const [mkPickerIdx, setMkPickerIdx] = useState<number | null>(null);

  /* extras — фиксированная длина 3, заполненная значениями. */
  const extrasFixed = [0, 1, 2].map((i) => p.extras[i] ?? "");
  const updateExtra = (idx: number, value: string) => {
    const next = [...extrasFixed];
    next[idx] = value;
    p.onExtrasChange(next.filter(Boolean));
  };

  return (
    <div className={css.form}>
      {p.conflictLead ? (
        <div className={css.conflict}>
          ⚠ Конфликт: лид «{p.conflictLead.clientName}» ({p.conflictLead.players} игр.) тоже хочет
          этот слот.
        </div>
      ) : null}

      {/* Row 1 — type pill + slot-time + source */}
      <div className={`${css.row} ${css.rowType}`}>
        <div className={css.bilead}>
          <Switch
            checked={!isLead}
            onChange={(on) => p.onTypeChange(on ? "booking" : "lead")}
            label={isLead ? "лид" : "бронь"}
            accent={isLead ? "var(--purple)" : "var(--green)"}
            variant="bilead"
            ariaLabel="Тип записи"
          />
        </div>
        {/* NB: CSS .bilead .label uppercases the text — pass lowercase here
            to read naturally in source; rendered as ЛИД / БРОНЬ. */}
        <div className={css.slotWrap}>
          <button
            type="button"
            className={`${css.input} ${css.inputNoChev} ${css.slotBtn} ${isLead ? css.slotLead : css.slotBooking} ${p.customTime ? css.slotCustomTime : ""}`}
            onClick={() => setSlotPickerOpen((v) => !v)}
            aria-haspopup="listbox"
            aria-expanded={slotPickerOpen}
          >
            {p.customTime || p.slotTime}
          </button>
          {slotPickerOpen ? (
            <SlotPicker
              questId={p.questId}
              bookings={p.dayBookings}
              slotTimes={p.questSlots}
              currentTime={p.customTime || p.slotTime}
              ownBookingId={p.bookingId}
              onPick={p.onSlotTimeChange}
              onClose={() => setSlotPickerOpen(false)}
            />
          ) : null}
        </div>
        <div className={css.sourceWrap}>
          <button
            type="button"
            className={`${css.input} ${css.inputSource} ${css.sourceBtn}`}
            onClick={() => setSourcePickerOpen((v) => !v)}
            aria-haspopup="listbox"
            aria-expanded={sourcePickerOpen}
          >
            {p.source || "— источник —"}
          </button>
          {sourcePickerOpen ? (
            <SourcePicker
              value={p.source}
              sources={p.leadSources}
              onPick={p.onSourceChange}
              onClose={() => setSourcePickerOpen(false)}
            />
          ) : null}
        </div>
      </div>

      {/* Row 2 — count + name + phone. Кол-во/телефон — пады; имя — клавиатура ОС. */}
      <div className={`${css.row} ${css.rowInputs}`}>
        <input
          readOnly
          inputMode="none"
          className={`${css.input} ${css.inputCount} ${p.validationErr && !p.players ? css.errorBorder : ""}`}
          value={p.players}
          data-pad-host=""
          onPointerDown={(e) => { e.preventDefault(); openPad("players", e.currentTarget); }}
          placeholder="Кол…"
          aria-label="Количество игроков"
          autoComplete="off"
        />
        <input ref={nameRef}
          className={`${css.input} ${p.validationErr && !staffGame && !p.clientName.trim() ? css.errorBorder : ""}`}
          value={p.clientName}
          onChange={(e) => p.onClientNameChange(e.target.value)}
          onFocus={() => setPad(null)} /* 06.09: клавиатура ОС поднялась — кастомный пад закрыть */
          placeholder="Имя…"
          aria-label="Имя клиента"
          autoComplete="off"
          spellCheck={false}
        />
        <PhoneInput
          value={p.phone}
          onChange={p.onPhoneChange}
          readonly
          padHost
          onTap={(e) => openPad("phone", e.currentTarget)}
          placeholder="+79…"
          hasError={!!p.validationErr && p.phone.replace(/\D/g, "").length < 11 && !(staffGame && !/\d/.test(p.phone))}
          ariaLabel="Телефон клиента"
        />
      </div>

      {/* Row 3 — ЧАЙНАЯ label + switch + 3 mk-cells (no extra icon — эталон) */}
      <div className={`${css.row} ${css.rowTea}`}>
        <span className={css.teaLbl}>ЧАЙНАЯ</span>
        <Switch
          checked={p.tea}
          onChange={p.onTeaChange}
          accent="var(--green)"
          ariaLabel="Чайная"
        />
        <div className={css.mkTrio}>
          {[0, 1, 2].map((i) => {
            const selectedId = extrasFixed[i] ?? "";
            const selectedMc = p.masterClasses.find((m) => String(m.id) === selectedId);
            return (
              <div key={i} className={css.mkSlot}>
                <button
                  type="button"
                  className={`${css.input} ${css.mkCell} ${selectedId ? "" : css.mkCellEmpty}`}
                  onClick={() => setMkPickerIdx(mkPickerIdx === i ? null : i)}
                  aria-haspopup="listbox"
                  aria-expanded={mkPickerIdx === i}
                  title={selectedMc ? selectedMc.name : undefined}
                >
                  <span className={css.mkLabel}>{selectedMc ? selectedMc.name : "ДОП"}</span>
                </button>
                {mkPickerIdx === i ? (
                  <MkPicker
                    value={selectedId}
                    options={p.masterClasses}
                    align={i === 0 ? "left" : i === 2 ? "right" : "center"}
                    onPick={(mcId) => updateExtra(i, mcId)}
                    onClose={() => setMkPickerIdx(null)}
                  />
                ) : null}
              </div>
            );
          })}
        </div>
      </div>

      {/* Row 4 — Здтк (+amt when on) + Класс + ДР + Учеб */}
      <div className={css.flagsRow}>
        <div className={css.prepaidCell}>
          <FlagToggle
            checked={p.prepaid}
            onChange={p.onPrepaidChange}
            label={p.prepaid ? "" : "Здтк"}
            tone="success"
          />
          {p.prepaid ? (
            <input
              readOnly
              inputMode="none"
              className={css.depMini}
              value={p.prepaidAmt}
              data-pad-host=""
              onPointerDown={(e) => { e.preventDefault(); openPad("dep"); }}
              aria-label="Сумма задатка"
            />
          ) : null}
        </div>
        <div className={css.tcell}>
          <FlagToggle checked={p.isClass} onChange={p.onClassChange} label="Класс" tone="success" />
        </div>
        <div className={css.tcell}>
          <FlagToggle checked={p.isBirthday} onChange={p.onBirthdayChange} label="ДР" tone="success" />
        </div>
        <div className={css.tcell}>
          <FlagToggle checked={p.isLearning} onChange={p.onLearningChange} label="Учеб" tone="success" />
        </div>
      </div>

      {/* Row 5 — !!! + КНКР + СпцВ + Инфо (edit-state) or empty (create) */}
      <div className={css.flagsRow3}>
        <div className={css.tcell}>
          <FlagToggle checked={p.isPriority} onChange={p.onPriorityChange} label="!!!" tone="alert" />
        </div>
        <div className={css.tcell}>
          <FlagToggle checked={p.isCompetition} onChange={p.onCompetitionChange} label="КНКР" tone="rival" />
        </div>
        <div className={css.tcell}>
          <FlagToggle checked={p.isSpecialEvent} onChange={p.onSpecialEventChange} label="СпцВ" tone="special" />
        </div>
        {p.onInfoClick ? (
          <div className={css.infoCell}>
            <button
              type="button"
              className={css.infoBtn}
              onClick={p.onInfoClick}
              aria-label="Информация о клиенте"
              title="Информация о клиенте"
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="7" cy="4.5" r="2.2" />
                <path d="M2.5 12 a4.5 4.5 0 0 1 9 0" />
              </svg><GameDotsFor bookingId={p.bookingId} phone={p.phone} max={4} />
            </button>
            <span className={css.infoLabel}>Инфо</span>
          </div>
        ) : <span aria-hidden="true" />}
      </div>

      {/* Спецверсия при включённом флаге СпцВ — кастомный пикер (A46-коррекция: select ОС снят) */}
      {p.isSpecialEvent ? (
        <div className={css.row} style={{ gridTemplateColumns: "1fr" }}>
          <SpecVersionPicker value={p.specialVersionId} options={p.specialVersions} onPick={p.onSpecialVersionChange} className={css.input} />
        </div>
      ) : null}
      {/* 13.10 (владелец): учебная игра при включённом «Учеб» — кто из сотрудников играет; по образцу ряда спецверсии */}
      {p.isLearning && p.learning ? <LearningPlayersRow lp={p.learning} inputClass={css.input} /> : null}

      {/* Row 6 — telegram */}
      <div className={css.row} style={{ gridTemplateColumns: "1fr" }}>
        <input
          readOnly
          inputMode="none"
          className={css.input}
          value={p.tg}
          data-pad-host=""
          onPointerDown={(e) => { e.preventDefault(); openPad("tg"); }}
          placeholder="@telegram или ID…"
          aria-label="Telegram клиента (username или ID)"
          autoComplete="off"
          spellCheck={false}
        />
      </div>

      {/* Row 7 — channel picker; long-press по ✈ — «Открыть переписку» (25.08, владелец) */}
      <div className={css.channelRow} style={{ position: "relative" }}>
        <span className={css.channelLabel}>КУДА НАПИСАТЬ</span>
        <ChannelPicker value={p.channel} onChange={p.onChannelChange} onCellLongPress={(id) => { if (id === "tg") setChatPop(true); }} />
        {chatPop ? (
          <div className={css.chatPop} data-chatpop="" onClick={(e) => e.stopPropagation()}>
            {(p.tg || "").trim()
              ? <button type="button" className={css.chatPopBtn} onClick={() => {
                  const v = (p.tg || "").trim();
                  /* числовой ID: вебвью режет tg:// — бот присылает нативную кнопку-открывашку (26.08) */
                  if (/^\d+$/.test(v.replace(/^@/, "")) && p.bookingId) void api(`/api/bookings/${p.bookingId}/open-chat`, { method: "POST" }).catch(() => {});
                  else openTgChat(v);
                  setChatPop(false);
                }}>✈ Открыть переписку {(p.tg || "").trim()}</button>
              : <div className={css.chatPopEmpty}>Telegram у брони не указан — привяжите контакт через бота или впишите @ник.</div>}
            {(p.phone || "").replace(/\D/g, "").length >= 10
              ? <button type="button" className={css.chatPopBtn} onClick={() => { openTgChatByPhone(p.phone ?? ""); setChatPop(false); }}>☎ Чат по номеру {p.phone}</button>
              : null}
            {/^\d+$/.test((p.tg || "").trim()) ? <div className={css.chatPopEmpty}>ID вместо ника: бот пришлёт вам в личку кнопку-открывашку.</div> : null}
          </div>
        ) : null}
      </div>

      {/* Row 8 — comment: docked M.6 (14.08), live-текст из пада. 06.09 (владелец): нативная каретка,
          «▎» снята; пад открывается по focus И по pointerdown без preventDefault; вставка через меню ОС.
          11.09 (Даша «вернулась обычная клавиатура»): readOnly держим ДО открытия пада — часть WebView
          не понимает inputMode="none". Снимаем при открытом паде: фокус уже стоит, ОС не просыпается. */}
      <AutoGrowTextarea ref={commentRef}
        minRows={2} maxHeight={240} inputMode="none" readOnly={pad !== "comment"}
        className={`${css.input} ${css.textarea}`}
        value={p.comment}
        onChange={(e) => p.onCommentChange(e.target.value)}
        onFocus={() => setPad("comment")}
        onPointerDown={() => { commentDown.current = true; setPad("comment"); }}
        onPointerUp={() => { commentDown.current = false; }}
        onPointerCancel={() => { commentDown.current = false; }}
        onPointerLeave={() => { commentDown.current = false; }}
        onClick={() => { const el = commentRef.current; if (el) window.setTimeout(() => scrollCommentAboveDock(el, dockH), 30); }}
        data-pad-host=""
        placeholder="Комментарий ДК…" aria-label="Комментарий диспетчера к бронированию"
      />

      {/* Audit log section (edit-state only). Lazy-loads on first expand. */}
      {p.bookingId ? <AuditHistory bookingId={p.bookingId} /> : null}

      {/* Спейсер: низ формы доскролливается над docked-падом; рендерится всегда, высота анимируется — закрытие пада не дёргает скролл рывком (24.08) */}
      <div style={{ height: pad === "comment" ? (dockH || 340) : 0, transition: "height 220ms ease" }} aria-hidden />
      <BookingFormPads p={p} pad={pad} anchor={padAnchor} commentRef={commentRef} onClose={closePad} onDockHeight={setDockH} />
    </div>
  );
}
